/**
 * Utility functions for optimizing state updates and reducing unnecessary renders
 */

// Deep equality check for screening status to prevent unnecessary updates
export const areScreeningStatusesEqual = (prev: any, current: any): boolean => {
  if (prev === current) return true;
  if (!prev || !current) return false;

  // Compare standard fields
  const relevantFields = [
    'status',
    'current_stock',
    'processed_count',
    'total_stocks',
    'passing_count',
    'failed_count',
    'error_count',
    'message'
  ];

  const fieldsEqual = relevantFields.every(field => prev[field] === current[field]);

  // BUG-002 FIX: Compare results separately for better performance
  // Quick check: compare array length and last item (for real-time updates)
  const prevResults = prev.results || [];
  const currentResults = current.results || [];
  const resultsEqual = (
    prevResults.length === currentResults.length &&
    (prevResults.length === 0 ||
     prevResults[prevResults.length - 1]?.ticker === currentResults[currentResults.length - 1]?.ticker)
  );

  return fieldsEqual && resultsEqual;
};

// Shallow comparison for screening results to detect meaningful changes
export const areScreeningResultsEqual = (prev: any[], current: any[]): boolean => {
  if (prev === current) return true;
  if (!prev || !current) return false;
  if (prev.length !== current.length) return false;

  // Quick length and first/last item check for performance
  if (prev.length === 0) return true;
  if (prev[0]?.ticker !== current[0]?.ticker) return false;
  if (prev.length > 1 && prev[prev.length - 1]?.ticker !== current[current.length - 1]?.ticker) return false;

  return true; // Arrays are likely the same if length and boundary checks pass
};

// Efficient database stats comparison
export const areDatabaseStatsEqual = (prev: any, current: any): boolean => {
  if (prev === current) return true;
  if (!prev || !current) return false;

  const relevantFields = [
    'total_stocks',
    'stocks_with_data',
    'stocks_with_targets',
    'latest_passing',
    'last_data_update'
  ];

  return relevantFields.every(field => prev[field] === current[field]);
};

// Smart throttling for polling updates
export class SmartThrottle {
  private lastUpdate: number = 0;
  private updateCount: number = 0;
  private readonly baseInterval: number;
  private readonly maxInterval: number;

  constructor(baseInterval: number = 1000, maxInterval: number = 5000) {
    this.baseInterval = baseInterval;
    this.maxInterval = maxInterval;
  }

  shouldUpdate(): boolean {
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastUpdate;

    // Calculate dynamic interval based on activity
    const dynamicInterval = Math.min(
      this.baseInterval + (this.updateCount * 100), // Increase interval with each update
      this.maxInterval
    );

    if (timeSinceLastUpdate >= dynamicInterval) {
      this.lastUpdate = now;
      this.updateCount++;
      return true;
    }

    return false;
  }

  reset(): void {
    this.updateCount = 0;
    this.lastUpdate = 0;
  }

  setActive(): void {
    // Reset to fast polling when activity detected
    this.updateCount = 0;
  }
}

// State diff utilities for minimizing updates
export const createStateDiff = (prevState: any, currentState: any) => {
  const diff: any = {};
  let hasChanges = false;

  for (const key in currentState) {
    if (prevState[key] !== currentState[key]) {
      diff[key] = currentState[key];
      hasChanges = true;
    }
  }

  return hasChanges ? diff : null;
};

// Batch update utility for combining multiple state changes
export class BatchUpdater {
  private pendingUpdates: Map<string, any> = new Map();
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly batchDelay: number;

  constructor(batchDelay: number = 50) {
    this.batchDelay = batchDelay;
  }

  addUpdate(key: string, value: any, dispatch: Function): void {
    this.pendingUpdates.set(key, { value, dispatch });

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.flushUpdates();
    }, this.batchDelay);
  }

  private flushUpdates(): void {
    for (const [, { value, dispatch }] of this.pendingUpdates) {
      dispatch(value);
    }

    this.pendingUpdates.clear();
    this.batchTimeout = null;
  }

  clearPending(): void {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    this.pendingUpdates.clear();
  }
}

// Memoization utility for expensive computations
export const createMemoizedFunction = <T extends (...args: any[]) => any>(
  fn: T,
  keySelector?: (...args: Parameters<T>) => string
): T => {
  const cache = new Map<string, ReturnType<T>>();

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = keySelector ? keySelector(...args) : JSON.stringify(args);

    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = fn(...args);
    cache.set(key, result);

    // Limit cache size to prevent memory leaks
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value as string;
      cache.delete(firstKey);
    }

    return result;
  }) as T;
};

// Performance monitoring utilities
export const performanceMonitor = {
  startTiming: (label: string): (() => number) => {
    const start = performance.now();
    return (): number => {
      const end = performance.now();
      const duration = end - start;
      console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
      return duration;
    };
  },

  measureAsync: async <T>(label: string, asyncFn: () => Promise<T>): Promise<T> => {
    const endTiming = performanceMonitor.startTiming(label);
    try {
      const result = await asyncFn();
      endTiming();
      return result;
    } catch (error) {
      endTiming();
      throw error;
    }
  }
};